import React, { useContext, useEffect, useRef, useState } from 'react'
import {
  DeleteGrantMutation,
  GrantStatus,
  GrantType,
  useAddEoiMutation,
  useCreateGrantMutation,
  useDeleteGrantMutation,
  useGetEoiForAllUsersQuery,
  useUpdateGrantMutation,
} from '../../../../generated/graphql'
import { checkIsActive, KTSVG } from '../../../../_metronic/helpers'
import { MenuComponent } from '../../../../_metronic/assets/ts/components'
import { graphqlRequestClient, queryClient } from '../../../../queries/client'
import Toast, { ToastType } from '../../../../utils/toast'
import { GlobalContext } from '../../store/context/globalContext'
import { getCurrency, getSymbolOfCurrency, formatNumber } from '../../../../utils/CurrencyFormatter'
import { DeleteConfirmPopup } from '../../../../utils/DeleteConfirmPopup'
import { VestingScheduleGraphPopup } from './VestingScheduleGraph'
import ESOPFinancingDrawer from './ESOPFinancing'

type VestingFrequency = 'Daily' | 'Weekly' | 'Monthly' | 'Quarterly' | 'Semi_Annually' | 'Annually'

type VestingScheduleData = {
  companyName: string
  grantDate: string | Date
  numberGranted: number
  vestedOptions:number
  cliffDurationMonths: number
  totalVestingDurationMonths: number
  vestingFrequency: VestingFrequency
  vestingTemplateName?: string
}

export function PlanNameRender(props: any) {
  const planName: string = props.data?.planName || ''

  return (
    <div className='d-flex align-items-center py-2'>
      <div className='d-flex flex-column' style={{ maxWidth: '100%' }}>
        <span 
          className='fw-bold text-dark' 
          style={{ 
            whiteSpace: 'nowrap', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            fontSize: '0.9rem' 
          }}
        >
          {planName || '—'}
        </span>
      </div>
    </div>
  )
}

export function CompanyNameRender(props: any) {
  const companyName: string = props.data?.companyName || ''
  const planName: string = props.data?.vestingSchedules?.[0]?.planName || ''

  const getInitials = (name: string) => {
    const words = name.trim().split(' ')
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase()
    return (words[0][0] + words[1][0]).toUpperCase()
  }

  const initials = getInitials(companyName)

  return (
    <div className='d-flex align-items-center gap-3 py-2'>
      {/* Initials Box */}
      <div
        className='d-flex align-items-center justify-content-center fw-bold'
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          backgroundColor: '#ffffff',
          color: '#a57ff8',
          fontSize: 16,
          border: '1px solid #e4e6ef',
        }}
      >
        {initials}
      </div>

      {/* Company Name + Plan Name */}
      <div className='d-flex flex-column'>
        <span className='fw-bold text-dark'>{companyName}</span>
        {planName ? (
          <span className='text-muted fs-7'>{planName}</span>
        ) : (
          <span className='text-muted fs-7'></span>
        )}
      </div>
    </div>
  )
}

export function GrantTypeRender(props: any) {
  return (
    <div className='py-3'>
      <p className='m-0' title={GrantType[props.value as keyof typeof GrantType]}>
        {GrantType[props.value as keyof typeof GrantType]}
      </p>
    </div>
  )
}

export function NumberGrantedRender(props: any) {
  const value = props.value
  const displayValue =
    value === 0 || value === null || value === undefined ? 'Pending Input' : value.toLocaleString()

  return (
    <div className='d-flex flex-column py-2'>
      <span className='fw-bold fs-6 text-dark'>
        {value === 0 ? 'Pending Input' : value.toLocaleString()}
      </span>
      <span className='text-muted fs-7'>{value === 0 ? 'To be updated' : 'Total options'}</span>
    </div>
  )
}

export function PriceValueRender(props: any) {
  const { state } = useContext(GlobalContext)
  const { userCurrency } = state

  const exercisePrice = Number(props.value ?? 0)
  const numberGranted = Number(props.data?.numberGranted ?? 0)
  const totalCost = numberGranted * exercisePrice

  const format = getCurrency(userCurrency)
  const symbol = getSymbolOfCurrency(userCurrency)

  return (
    <div className='py-3'>
      <p className='m-0 fw-bold text-dark' title={`${symbol} ${exercisePrice.toFixed(2)}`}>
        {symbol} {formatNumber({ value: exercisePrice, format })}
      </p>

      <span className='text-muted small'>
        {symbol} {formatNumber({ value: totalCost, format })}
      </span>
    </div>
  )
}

export function ExpiryDateRender(props: any) {
  const formattedDate = props.value ? new Date(props.value).toLocaleDateString() : '-'
  return (
    <div className='py-3'>
      <p className='m-0' title={formattedDate}>
        {formattedDate}
      </p>
    </div>
  )
}

export function VestedUnvestedRenderer(props: any) {
  const { state } = useContext(GlobalContext)
  const { userCurrency } = state

  const granted = props.data?.numberGranted ?? 0
  const vested = props.data?.vestedOptions ?? 0
  const unvested = Math.max(granted - vested, 0)
  const fmv = Number(props.data?.companyFMV ?? 0)

  const isPending = granted === 0 && vested === 0

  if (isPending) {
    return <span style={{ color: '#999' }}></span>
  }

  const format = getCurrency(userCurrency)
  const symbol = getSymbolOfCurrency(userCurrency)

  const vestedValue = fmv * vested
  const unvestedValue = fmv * unvested

  return (
    <span>
      <div className='py-3'>
        <div style={{ fontSize: '12px', lineHeight: '1.2' }}>
          <div>
            <strong>{vested}</strong> vested{' '}
            <span className='text-muted'>
              ({symbol} {formatNumber({ value: vestedValue, format })})
            </span>
          </div>
          <div style={{ color: '#888' }}>
            {unvested} unvested{' '}
            <span className='text-muted'>
              ({symbol} {formatNumber({ value: unvestedValue, format })})
            </span>
          </div>
        </div>
      </div>
    </span>
  )
}

export function GrantStatusRender(props: any) {
  const status = props.value
  const isActive = status === 'Active'

  const backgroundColor = isActive ? '#e8fff3' : '#fff5f8'
  const textColor = isActive ? '#000000' : '#000000'
  return (
    <span
      className='fw-semibold text-capitalize'
      style={{
        backgroundColor,
        color: textColor,
        padding: '4px 12px',
        borderRadius: '999px',
        fontSize: '12px',
        display: 'inline-block',
      }}
    >
      {status.toLowerCase()}
    </span>
  )
}

export function ActionRender(props: any) {
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [showVestingGraph, setShowVestingGraph] = useState(false)
  const [showFinancing, setShowFinancing] = useState(false)

  // Reinitialize dropdown menu on props change
  useEffect(() => {
    setTimeout(() => {
      MenuComponent.reinitialization()
    }, 50)
  }, [props])

  // Delete Grant Mutation
  const deleteGrantMutation = useDeleteGrantMutation(graphqlRequestClient, {
    onSuccess: (_, variables) => {
      if (props?.context?.onGrantDeleted && variables.grantId) {
        props.context.onGrantDeleted(variables.grantId.toString())
      }
      queryClient.invalidateQueries()
      Toast('Grant deleted successfully', ToastType.success)
    },
    onError: (error: any) => {
      Toast(error.response?.errors?.[0]?.message || 'Delete failed', ToastType.error)
    },
  })

  // Add EOI (ESOP Financing) Mutation
  const addEoiMutation = useAddEoiMutation(graphqlRequestClient, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: useGetEoiForAllUsersQuery.getKey() })
      Toast('ESOP Financing request submitted successfully!', ToastType.success)
      setShowFinancing(false)
    },
    onError: (error: any) => {
      Toast(
        error.message || 'Failed to submit financing request. Please try again.',
        ToastType.error
      )
      console.error('ESOP Financing submission error:', error)
    },
  })

  const isSubmitting = addEoiMutation.isLoading 
  const submitError =
    addEoiMutation.error &&
    ((addEoiMutation.error as any)?.message ||
      (addEoiMutation.error as any)?.response?.errors?.[0]?.message ||
      'Something went wrong. Please try again.')

  const handleDelete = () => {
    const grantId = props.data?.grantId
    if (!grantId) return
    deleteGrantMutation.mutate({ grantId })
  }

  const handleEdit = () => props?.context?.openEditModal?.(props.data)
  const handleOpenCalculator = () => props?.context?.onOpenCalculator?.(props.data)

  const hasVestingScheduleAlt = () => {
    const grant = props.data
    const vestingSchedule = grant?.vestingSchedules?.[0]
    return (
      grant?.status === 'Active' &&
      vestingSchedule &&
      vestingSchedule.cliffDurationMonths >= 0 &&
      vestingSchedule.totalVestingDurationMonths > 0 &&
      vestingSchedule.vestingFrequency &&
      grant.grantDate &&
      grant.numberGranted > 0
    )
  }

  const getVestingData = (): VestingScheduleData => {
    const grant = props.data
    const vestingSchedule = grant?.vestingSchedules?.[0]

    return {
      companyName: grant?.companyName || '',
      grantDate: grant?.grantDate || new Date(),
      numberGranted: grant?.numberGranted || 0,
      vestedOptions: grant?.vestedOptions || 0,
      cliffDurationMonths: vestingSchedule?.cliffDurationMonths || 0,
      totalVestingDurationMonths: vestingSchedule?.totalVestingDurationMonths || 0,
      vestingFrequency: vestingSchedule?.vestingFrequency || 'Monthly',
      vestingTemplateName: vestingSchedule?.vestingTemplateName || undefined,
    }
  }

  const handleViewVestingGraph = () => {
    if (hasVestingScheduleAlt()) {
      setShowVestingGraph(true)
    } else {
      Toast('No valid time-based vesting schedule available for this grant.', ToastType.warning)
    }
  }

  const handleOpenFinancing = () => {
    setShowFinancing(true)
  }

  const handleFinancingSubmit = async (data: { esopFinancing: any; companyName: string }) => {
    await addEoiMutation.mutateAsync({
      input: {
        esopFinancing: data.esopFinancing,
        companyName: data.companyName,
        offerType: 'ESOP_FINANCING',
      },
    })
  }

  const getMenuOptions = () => {
    const baseOptions = ['Calculate Exercise', 'Edit', 'Delete']

    if (hasVestingScheduleAlt()) {
      return ['View Vesting Graph', ...baseOptions]
    }

    return baseOptions
  }

  const actionMenu = () => {
    const options = getMenuOptions()

    return options.map((option) => (
      <label
        key={option}
        className="form-check-custom py-3 bg-hover-primary text-hover-white px-3 cursor-pointer"
        onClick={() => {
          if (option === 'Delete') {
            setShowConfirmDelete(true)
          } else if (option === 'Edit') {
            handleEdit()
          } else if (option === 'Calculate Exercise') {
            handleOpenCalculator()
          } else if (option === 'View Vesting Graph') {
            handleViewVestingGraph()
          } else if (option === 'ESOP Financing') {
            handleOpenFinancing()
          }
        }}
      >
        <div className="d-flex align-items-center">
          <span className="form-check-label fs-base ms-6 fw-normal">{option}</span>
        </div>
      </label>
    ))
  }

  return (
    <div className="px-0" style={{ zIndex: 999 }}>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmPopup
        showModel={showConfirmDelete}
        setShowModel={setShowConfirmDelete}
        loading={deleteGrantMutation.isLoading}
        title="Delete Grant"
        description="Are you sure you want to permanently delete this grant?"
        notes="This action cannot be undone."
        confirmText="Type DELETE GRANT to confirm"
        currentValue="DELETE GRANT"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        primaryAction={() => {
          handleDelete()
          setShowConfirmDelete(false)
        }}
      />

      {/* Vesting Graph Modal */}
      {showVestingGraph && (
        <VestingScheduleGraphPopup
          showModal={showVestingGraph}
          handleClose={() => setShowVestingGraph(false)}
          vestingData={getVestingData()}
        />
      )}

      {/* ESOP Financing Drawer */}
      {showFinancing && (
        <ESOPFinancingDrawer
          isOpen={showFinancing}
          onClose={() => setShowFinancing(false)}
          onConfirm={handleFinancingSubmit}
          isSubmitting={isSubmitting}
          submitError={submitError}
          totalOptionsAvailable={props.data?.vestedOptions || 0}
          grants={[props.data]}
          preSelectedGrant={props.data}
        />
      )}

      {/* Action Menu Button */}
      <div className="px-0">
        <button
          type="button"
          className="btn btn-sm btn-icon btn-color-gray-400 btn-active-secondary"
          data-kt-menu-trigger="click"
          data-kt-menu-placement="bottom-end"
          data-kt-menu-flip="top-end"
        >
          <KTSVG path="/media/icons/duotune/general/gen052.svg" className="svg-icon-muted svg-icon-2" />
        </button>

        <div
          className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg-light-primary fw-bold w-200px py-3 z-999"
          data-kt-menu="true"
        >
          {actionMenu()}
        </div>
      </div>
    </div>
  )
}